// Product analytics — client-side event tracker.
//
// Gate: VITE_JINGLE_PRODUCT_EVENTS_ENABLED (default "true").
// Set to "false" to disable all client-side analytics in any env.
//
// Behaviour:
//   - Fails silently on any network or parse error.
//   - Debounces repeat fires of the same event within a short window
//     (2 seconds) to avoid double-counting surface-entry effects.
//   - Never sends raw form content, clue text, photo paths, or any
//     value not in the explicit metadata set passed by the caller.
//   - Uses the existing credentials: "include" pattern so the server
//     can resolve tenantId / userId from the session cookie.

const DEBOUNCE_MS = 2_000;
const debounceMap = new Map<string, number>();

function isEnabled(): boolean {
  const raw = import.meta.env.VITE_JINGLE_PRODUCT_EVENTS_ENABLED;
  if (raw === "false" || raw === "0") return false;
  return true;
}

export type ClientProductEventName =
  | "home_viewed"
  | "launchpad_viewed"
  | "santa_letter_viewed"
  | "hunt_list_viewed"
  | "clue_opened"
  | "voice_play_requested"
  | "voice_play_completed"
  | "map_viewed"
  | "inbox_viewed"
  | "recap_viewed"
  | "memory_shelf_viewed"
  | "first_clue_viewed"
  // P1 first-night fix — host-only banner on /home that deep-links into
  // Mission Control when the family's mission has approvedCluesTotal===0.
  // No PII in either event's metadata; server resolves tenantId from
  // the authenticated session.
  | "home_approve_first_clue_cta_viewed"
  | "home_approve_first_clue_cta_clicked"
  // Cold-traffic readiness phase 1 — canonical back-half funnel events.
  // These let paid Meta/Google dashboards measure activation past the
  // pre-existing `first_magical_value_viewed` boundary without leaking
  // PII (server resolves tenantId from the authenticated session; the
  // SafeClientMetadata type already precludes object payloads).
  | "mission_preview_started"
  | "mission_preview_completed"
  | "save_mission_click"
  | "account_completed"
  | "mission_created"
  | "family_invite_created"
  | "family_member_joined"
  | "mission_started"
  | "feedback_link_clicked"
  | "support_link_clicked"
  // Marketing / public funnel events (unauthenticated)
  | "marketing_home_viewed"
  | "pricing_viewed"
  | "faq_viewed"
  | "early_access_clicked"
  | "waitlist_submitted"
  | "contact_clicked";

export type SafeClientMetadata = Record<
  string,
  string | number | boolean | null
>;

/**
 * Fire a client-side product analytics event.
 * Debounced per event name, fails silently, never throws.
 */
export function trackProductEvent(
  eventName: ClientProductEventName,
  metadata?: SafeClientMetadata,
): void {
  if (!isEnabled()) return;

  const now = Date.now();
  const key = eventName;
  const last = debounceMap.get(key);
  if (last !== undefined && now - last < DEBOUNCE_MS) return;
  debounceMap.set(key, now);

  void (async () => {
    try {
      await fetch("/api/product-events", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          eventName,
          eventSource: "client",
          metadata: metadata ?? {},
        }),
      });
    } catch {
      // Swallow — analytics must never break the page.
    }
  })();
}
